<template>
    <div class="flex flex-col h-full overflow-hidden">
        <div ref="chatContainer" class="flex-1 overflow-auto p-3 bg-elements border border-border flex flex-col gap-2">
            <div class="flex-grow"></div>
            <CaseChatMessage
                v-for="m in messages"
                :key="m.id"
                :message="m"
                :users-map="usersMap"
                :guest-name="guestName"/>
        </div>
        <form class="flex flex-col gap-2 p-3 bg-elements border border-border" @submit.prevent="send">
            <InputTextArea ref="messageInput"
                           v-model="message"
                           label="Har du spørgsmål?"
                           name="message"
                           placeholder="Skriv en besked..."
                           class="flex-1"
                           rows="3"/>
            <CallToAction type="submit"
                          class="text-14"
                          :disabled="!canSend"
                          variant="blue"
                          :loading="loading">
                Send
            </CallToAction>
        </form>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, toRefs, watch } from 'vue';
import { GroupChatMessageViewObject } from '@/api';
import communicationApiService from '@/core/api/controllers/communicationApi.service';
import bus, { useBusEvents } from '@/core/bus';
import { useUsers } from '@/core/compositions/users.compositions';
import CaseChatMessage from './CaseChatMessage.vue';
import { throttle } from 'lodash-es';
import InputTextArea from '@/core/forms/inputs/InputTextArea.vue';
import notificationsService from '@/core/notifications/notifications.service';
import { userStore } from '@/core/store/user/user.store';

const props = defineProps<{ chatId: string; guestName?: string | null; guestEmail?: string | null }>();
const { chatId, guestName, guestEmail } = toRefs(props);

const messages = ref<GroupChatMessageViewObject[]>([]);
const skip = ref(0);
const total = ref(0);
const loading = ref(false);
const chatContainer = ref<HTMLElement | null>(null);
const messageInput = ref<InstanceType<typeof InputTextArea> | null>(null);
const MESSAGES_TAKE = 20;
const isGuestChat = computed(() => !userStore.user.value);

const { usersMap } = useUsers(computed(() =>
    messages.value
        .map(m => m.senderUserId)
        .filter((id): id is string => typeof id === 'string')
        .filter((v, i, a) => a.indexOf(v) === i)));

async function load(clear: boolean) {
    if (loading.value) 
        return;

    if (clear) {
        messages.value = [];
        skip.value = 0;
        total.value = 0;
    }

    loading.value = true;

    // --- Preserve scroll position if appending ---
    let prevScrollHeight = 0;
    if (!clear && chatContainer.value) {
        prevScrollHeight = chatContainer.value.scrollHeight;
    }
    
    if (!chatId.value) {
        loading.value = false;
        return;
    }

    if (clear)
        await markAsRead();

    const result = await communicationApiService.getMessagesForChat(chatId.value, skip.value, MESSAGES_TAKE);
    if (result) {
        messages.value.unshift(...result.messages);
        total.value = result.total;
        skip.value += MESSAGES_TAKE;
        if (clear) {
            scrollToBottom(false);
        }
    }

    if (!clear && chatContainer.value) {
        await nextTick();
        const newScrollHeight = chatContainer.value.scrollHeight;
        chatContainer.value.scrollTop = newScrollHeight - prevScrollHeight;
    }

    loading.value = false;
}

function scrollToBottom(smooth: boolean) {
    setTimeout(() => {
        if (chatContainer.value) {
            chatContainer.value.scrollTo({ top: chatContainer.value.scrollHeight, behavior: smooth ? 'smooth' : undefined });
        }
    }, 15);
}

async function markAsRead() {
    if (!userStore.user.value)
        return;

    if (!chatId.value)
        return;

    const result = await communicationApiService.markChatAsRead(chatId.value);
    if (result) {
        messages.value.forEach(m => {
            if (!m.readBy.includes(userStore.user.value!.id)) {
                m.readBy.push(userStore.user.value!.id);
            }
        });

        bus.emit('ChatMarkedAsRead', chatId.value);
    }
}

const message = ref('');
const canSend = computed(() => {
    const hasMessage = !!message.value.trim();
    if (!hasMessage)
        return false;

    if (isGuestChat.value)
        return !!guestName.value && !!guestEmail.value;

    return true;
});
async function send() {
    const value = message.value.trim();
    if (!value || loading.value)
        return;

    if (isGuestChat.value && (!guestName.value || !guestEmail.value)) {
        notificationsService.notify({
            title: 'Chat',
            message: 'Angiv venligst dit navn og din e-mail, før du sender en besked.',
            type: 'Warning',
        });
        return;
    }

    if (!chatId.value) {
        return;
    }

    loading.value = true;
    try {
        await communicationApiService.sendChatMessage({
            chatId: chatId.value,
            message: value,
            senderName: guestName.value,
            senderEmail: guestEmail.value,
        });
        message.value = '';
        messageInput.value?.focusInput();
    } finally {
        loading.value = false;
    }
}

useBusEvents({
    ChatMessageReceived: async(m: GroupChatMessageViewObject) => {
        if (m.chatId === chatId.value) {
            messages.value.push(m);
            total.value = Math.max(total.value + 1, messages.value.length);
            scrollToBottom(true);
            await markAsRead();
        }
    },
    SIGNALR_RECONNECT: () => load(true),
});

watch(() => chatId.value, () => load(true));

// Throttle scroll handler to avoid rapid triggers
const handleScroll = throttle(async() => {
    if (!chatContainer.value || loading.value) 
        return;

    // If scrolled to the top, fetch older messages
    if (chatContainer.value.scrollTop === 0 && messages.value.length < total.value) {
        skip.value = messages.value.length;
        await load(false);
        await nextTick();
    }
}, 300);

onMounted(async() => {
    await load(true);
    await nextTick();
    // Scroll to bottom on initial load
    if (chatContainer.value) {
        chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
        chatContainer.value.addEventListener('scroll', handleScroll);
    }
});

onBeforeUnmount(() => {
    if (chatContainer.value) {
        chatContainer.value.removeEventListener('scroll', handleScroll);
    }
});
</script>

<style lang="scss" scoped>
</style>
